/**
 * 企業（is_corporate）判定の単一ソース。
 *
 * WTT の組織区分は (is_company, is_corporate) の2フラグで3区分を表す:
 *   学校      = (0, 0)
 *   インターン = (1, 0)  ← 非学校扱い・お悩みラベルは企業バリアント継承（#3238）
 *   企業      = (1, 1)  ← 企業固有動作（お悩みサブ項目）のゲート対象
 *
 * is_company は「非学校」全般の表示制御に使われ続けるため、サブ項目ゲートだけを
 * この純関数で企業（両フラグ truthy）に絞る。is_company 単独ゲートに巻き戻すと
 * インターン(id=18)でもサブ項目が出てしまう（要件2違反）ので、ここを唯一の判定点とし
 * inline テンプレ・メソッド/computed の双方から参照して散在を防ぐ。
 *
 * commonFunctions(catch-all・Vuex store からも import)肥大を避けるための専用 mixin。
 */
export default {
  methods: {
    /**
     * 所属校配列が「企業」かを判定する純関数。
     * @param {Array<{is_company:number, is_corporate:number}>} schoolArr 所属校配列（user.school 相当）
     * @returns {boolean} is_company かつ is_corporate のとき true
     */
    isCorporateSchool(schoolArr) {
      return !!schoolArr?.[0]?.is_company && !!schoolArr?.[0]?.is_corporate;
    },
    /**
     * 閲覧者自身が「企業組織の管理者（主/副）」かを判定する純関数。
     * 共有画面で管理者(11/12)だけを落としたい表示ゲートに使う。
     *
     * レコード所有者の所属校ではなく閲覧中ユーザーの所属校で見るのは、同じ共有行を通る
     * 事務局(31)・医師心理士(21/22)が企業利用者のレコードを開いたときに巻き込まないため。
     * 所有者軸に倒すと、事務局・医師心理士の画面でも企業利用者の行だけゲートが効いてしまう。
     *
     * user 解決前（loginCheck 応答前で school 未ロード）は false を返す＝素通り側に倒れるので、
     * ゲートを置く側は user 解決後に再判定する経路を必ず持たせること。
     * @param {{account_type:number, school:Array}} user ログイン中ユーザー（Vuex user state）
     * @returns {boolean} 企業組織に所属する主管理者/副管理者のとき true
     */
    isCorporateOrgAdmin(user) {
      return [11, 12].includes(user?.account_type) && this.isCorporateSchool(user?.school);
    },
    /**
     * 組織区分（is_company, is_corporate）から3区分 orgType を導出する純関数。
     * 登録/更新/一覧の3画面で散在していた導出をここに集約（将来 org_type カラム化時の改修を1ファイルに閉じる）。
     * @param {{is_company:number, is_corporate:number}} school 学校レコード（または相当オブジェクト）
     * @returns {number} 0=学校 / 1=インターン / 2=企業
     */
    orgTypeOf(school) {
      if (school && school.is_company && school.is_corporate) return 2;
      if (school && school.is_company) return 1;
      return 0;
    },
    /**
     * 3区分 orgType から (is_company, is_corporate) を導出する純関数（orgTypeOf の逆写像）。
     * school_type は「非学校(インターン/企業)=6固定 / 学校=呼び出し側で選択」のため呼び出し側で扱う。
     * @param {number} orgType 0=学校 / 1=インターン / 2=企業
     * @returns {{is_company:number, is_corporate:number}}
     */
    orgTypeFlags(orgType) {
      if (orgType === 2) return { is_company: 1, is_corporate: 1 };
      if (orgType === 1) return { is_company: 1, is_corporate: 0 };
      return { is_company: 0, is_corporate: 0 };
    },
  },
};
